import { format } from 'date-fns';
import { Range } from './range';

// Helper to format date as dd/MM/yy, HH:mm
export function formatDate(dateString :string) {
if (!dateString) return '';
const date = new Date(dateString);
if (isNaN(date.getTime())) return '';
return format(date, 'dd/MM/yy, HH:mm');
}

export function formatDateToISO(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd');
}

export function formatShortDate (date: Date){
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export const getFormattedPreviousYearDate = (dateObj: Date) => {
  if (!(dateObj instanceof Date)) return "";

  // Create a new Date instance to avoid mutating the original object
  const pastDate = new Date(dateObj.getTime());
  
  // Subtract one year
  pastDate.setFullYear(pastDate.getFullYear() - 1);

  return formatDateToISO(pastDate);
};

export const getPreviousYearDate = (dateObj: Date): Date | null => {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return null;
  }

  // Create a new Date instance to avoid mutating the original object
  const pastDate = new Date(dateObj.getTime());
  
  // Subtract one year
  pastDate.setFullYear(pastDate.getFullYear() - 1);

  return pastDate;
};

export function toShortDate(date: Date){
    if (!date) return '';
    return format(date, 'yyyy-MM-dd HH:mm');
};


export  const checkAndFormatDate = (dateStr: string) => {
    if (!dateStr) return "";

    // Check if the date is already in DD/MM/YYYY format
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateStr.match(datePattern);
    if (match) {
      return `${match[1].padStart(2, "0")}/${match[2].padStart(2, "0")}/${match[3].slice(-2)}`;
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export const getSafeDate = (dateStr: string) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const newDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      return isNaN(newDate.getTime()) ? null : newDate;
    }
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

export const extractDateTime = (date: Date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.log('Invalid date provided');
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}


export const getDatesByRange = (range:Range | undefined, t: (key: string) => string) => {
  const today = new Date();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: "long", 
    day: "numeric", 
    month: "long", 
    year: "numeric" 
  };

  const formatDate = (date: Date, options = defaultOptions) =>
  date.toLocaleDateString("fr-FR", options);
  switch (range) {
    case Range.Direct:
      return capitalize(formatDate(today));

    case Range.PreviousDay: {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return capitalize(formatDate(yesterday));
    }

    case Range.CurrentWeek: {
      const startOfWeek = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      const todayLabel = t('common.today') || '';
      const todayLabelLowerCase = todayLabel ? todayLabel.charAt(0).toLowerCase() + todayLabel.slice(1) : '';
      return capitalize(`${formatDate(startOfWeek)} ${t('common.until')} ${todayLabelLowerCase}`);
    }

    case Range.LastWeek: {
      const startOfLastWeek = new Date(today);
      const endOfLastWeek = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      startOfLastWeek.setDate(diff - 7);
      endOfLastWeek.setDate(diff - 1);
      return capitalize(`${formatDate(startOfLastWeek)} ${t('common.until_au')} ${formatDate(endOfLastWeek)}`);
    }

    case Range.CurrentMonth: {
      return capitalize(
        formatDate(today, { month: "long", year: "numeric" })
      );
    }
    case Range.LastMonth: {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return capitalize(
        formatDate(lastMonth, { month: "long", year: "numeric" })
      );
    }
  }
  return ""
};

export const getShortDatesByRange = (
  range: Range | undefined,
  t: (key: string) => string,
  isSmallScreen: boolean = typeof window !== "undefined" && window.innerWidth <= 844
) => {
  const today = new Date();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: "short", 
    day: "numeric", 
    month: "short", 
    year: "numeric" 
  };

  const formatDate = (date: Date, options = defaultOptions) => {
    const formatted = date.toLocaleDateString("fr-FR", options);
    return formatted.split(' ').map(part => {
      if (/^[a-zA-ZÀ-ÿ]/.test(part)) {
        return capitalize(part);
      }
      return part;
    }).join(' ');
  };

  switch (range) {
    case Range.Direct:
      return formatDate(today);

    case Range.PreviousDay: {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return formatDate(yesterday);
    }

    case Range.CurrentWeek: {
      const startOfWeek = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      const todayLabel = t('common.today') || '';
      const todayLabelLowerCase = todayLabel ? todayLabel.charAt(0).toLowerCase() + todayLabel.slice(1) : '';
      return capitalize(`${formatDate(startOfWeek)} ${t('common.until')} ${todayLabelLowerCase}`);
    }

    case Range.LastWeek: {
      const startOfLastWeek = new Date(today);
      const endOfLastWeek = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      startOfLastWeek.setDate(diff - 7);
      endOfLastWeek.setDate(diff - 1);
      const startOptions: Intl.DateTimeFormatOptions = isSmallScreen
        ? { weekday: "short", day: "numeric", month: "short" }
        : defaultOptions;
      return capitalize(`${formatDate(startOfLastWeek, startOptions)} ${t('common.until_au')} ${formatDate(endOfLastWeek)}`);
    }

    case Range.CurrentMonth: {
      return formatDate(today, { month: "short", year: "numeric" });
    }
    case Range.LastMonth: {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return formatDate(lastMonth, { month: "short", year: "numeric" });
    }
  }
  return ""
};

const capitalize = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};


export const getLabelDate = (date: Date | null): string => {
  const targetDate = date || new Date();
  return capitalize(targetDate.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }));
};

export const formatTimeLabel = (dateString?: string): string => {
  if (!dateString) return "--";
  const date = new Date(dateString.replace(" ", "T"));

  if (isNaN(date.getTime())) {
    return "--";
  }

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).replace(":", "h");
};


  // Format today's date in French (e.g., "Mardi 23 juin 2026")
  export const todayFormatted = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(new Date());
